import { useCallback, useEffect, useRef, useState } from 'react';

import { useLocation, useSearchParams } from 'react-router';

import { withCrossTabLock } from '@/lib/utils';
import { TOKEN_REFRESH_LOCK } from '@/store/baseApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { useGetMeQuery, useLogoutMutation, useRefreshTokenMutation } from './store/api';
import {
  selectIsAdmin,
  selectIsAuthenticated,
  selectIsSuspended,
  selectNeedsOrgSetup,
  selectUser,
  setToken,
  signOut,
  updateUser,
} from './store/authSlice';
import { getCapturedUrlToken } from './urlToken';

export function useCurrentUser() {
  return useAppSelector(selectUser);
}

export function useIsAuthenticated() {
  return useAppSelector(selectIsAuthenticated);
}

export function useIsAdmin() {
  return useAppSelector(selectIsAdmin);
}

export function useIsSuspended() {
  return useAppSelector(selectIsSuspended);
}

export function useNeedsOrgSetup() {
  return useAppSelector(selectNeedsOrgSetup);
}

/**
 * Re-syncs the persisted user from `/me` while signed in, so the guards don't
 * trust a stale or missing user (e.g. a refresh mid-login, or staff assigning
 * an org in Django admin). Fetches on every mount of the calling layout.
 *
 * `isUserLoaded` is false while there is a token but no user yet; callers
 * should render a loading state then, not the portal. `isError` is only set
 * when there is still no user to fall back on. A 401 is handled by baseApi
 * (refresh, then sign out).
 */
export function useSyncCurrentUser({ refetchOnFocus = false } = {}) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const { data, isError, refetch } = useGetMeQuery(undefined, {
    skip: !isAuthenticated,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!data) return;
    // Demo accounts can read /me but have no portal access; login blocks them too.
    if (data.user.is_demo) {
      dispatch(signOut());
      return;
    }
    dispatch(updateUser(data.user));
  }, [data, dispatch]);

  useEffect(() => {
    if (!refetchOnFocus || !isAuthenticated) return;
    const onFocus = () => {
      refetch();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetchOnFocus, isAuthenticated, refetch]);

  return {
    isUserLoaded: user !== null,
    isError: isError && user === null,
    retry: refetch,
  };
}

export function useSignOut() {
  const dispatch = useAppDispatch();
  const [logout] = useLogoutMutation();

  return async () => {
    try {
      await logout().unwrap();
    } catch {
      // Local state is cleared below even if the server call fails.
    }
    dispatch(signOut());
  };
}

type RestoreStatus = 'restoring' | 'done' | 'failed';

/**
 * The access token lives only in memory, so a page load starts without one. This asks the API for a new one with the HttpOnly refresh cookie, once per page load, holding the same cross-tab lock as every other refresh so tabs loading together don't race the cookie rotation.
 *
 * A 401 means there is no valid cookie, so it signs out locally. Any other failure (network, 5xx) leaves the session alone and reports `isError`, since the cookie may still be valid; `retry` tries again. Callers should not render routes while `isRestoring`.
 */
export function useRestoreSession() {
  const dispatch = useAppDispatch();
  const [refreshToken] = useRefreshTokenMutation();
  const [status, setStatus] = useState<RestoreStatus>('restoring');
  // A second refresh with the same cookie would be refused after rotation (StrictMode re-runs effects).
  const hasStarted = useRef(false);

  const restore = useCallback(() => {
    setStatus('restoring');
    withCrossTabLock(TOKEN_REFRESH_LOCK, () => refreshToken().unwrap())
      .then(({ access_token }) => {
        dispatch(setToken(access_token));
        setStatus('done');
      })
      .catch((err: unknown) => {
        if ((err as { status?: unknown } | null)?.status === 401) {
          dispatch(signOut());
          setStatus('done');
          return;
        }
        setStatus('failed');
      });
  }, [dispatch, refreshToken]);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    restore();
  }, [restore]);

  return { isRestoring: status === 'restoring', isError: status === 'failed', retry: restore };
}

/**
 * Returns the `token` query param of this page and makes sure it is not left in the address bar or in history. On a full page load `captureUrlToken` has already moved it into memory before the app rendered; on an in-app navigation it is read here and removed from the URL.
 */
export function useUrlToken() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const [token] = useState(() => searchParams.get('token') ?? getCapturedUrlToken(pathname) ?? '');

  useEffect(() => {
    if (!searchParams.has('token')) return;
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete('token');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  return token;
}
